import { useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Keyboard, Pressable, ScrollView, StyleSheet } from "react-native";
import type { TextInput } from "react-native-gesture-handler";
import { CouponItem, StepappWallet } from "../../src/components";
import { useAppDispatch, useAppSelector, useRedeemCoupon } from "../../src/hooks";
import { setDraftVoucher } from "../../src/store/draftReservation";
import { setUserData } from "../../src/store/user";

import {
  type StoredDiscountFragment,
  isWeb,
  useGetUserProfileLazyQuery,
  useGetVirtualWalletBalanceQuery,
  useListOwnedCouponsQuery,
} from "@stepapp/core";
import {
  Block,
  Button,
  Divider,
  Input,
  Loader,
  Modal,
  Snackbar,
  Typography,
  palette,
  spacing,
} from "@stepapp/ui";

type CouponModalType = "success" | "error" | undefined;
type SnackbarRef = {
  hide: () => void;
} | null;

const CouponsScreen = () => {
  const { t } = useTranslation();
  const { back } = useRouter();
  const isLogged = useAppSelector(({ user }) => !!user?.id);
  const appDispatch = useAppDispatch();
  const inputRef = useRef<TextInput>(null);
  const snackbarRef = useRef<SnackbarRef>(null);
  const { data: virtualWalletBalance } = useGetVirtualWalletBalanceQuery({ skip: !isLogged });
  const { data, loading, refetch } = useListOwnedCouponsQuery({ skip: !isLogged });

  const [code, setCode] = useState("");
  const [couponModalVisible, setCouponModalVisible] = useState(false);
  const [couponModalType, setCouponModalType] = useState<CouponModalType>(undefined);

  const isDisabled = code?.length === 0;
  const coupons = useMemo(
    () => data?.listOwnedCoupons?.filter(Boolean) as StoredDiscountFragment[],
    [data],
  );

  const handleCouponPress = useCallback(
    (coupon: StoredDiscountFragment | null) => {
      coupon && back();
      appDispatch(setDraftVoucher(coupon?.code || ""));
    },
    [appDispatch, back],
  );

  const [getUserProfile] = useGetUserProfileLazyQuery({
    variables: {
      isCustomerProfile: true,
    },
  });

  const handleUserFetch = useCallback(async () => {
    const { data } = await getUserProfile();

    data?.getMyProfile && appDispatch(setUserData(data?.getMyProfile));
  }, [appDispatch, getUserProfile]);

  const { redeemCoupon, loadingCoupon, title, description } = useRedeemCoupon({
    onSuccess: ({ isVoucher }) => {
      setCouponModalType("success");
      setCouponModalVisible(true);
      setCode("");
      isVoucher ? refetch() : handleUserFetch();
    },
    onError: () => {
      setCouponModalType("error");
      setCouponModalVisible(true);
      setCode("");
    },
  });

  const handleRedeemCoupon = useCallback(() => {
    Keyboard.dismiss();
    redeemCoupon(code);
  }, [code, redeemCoupon]);

  const hideSnackbar = useCallback(() => snackbarRef?.current?.hide(), []);

  const handleOnAddPress = useCallback(() => {
    inputRef?.current?.focus();

    if (couponModalVisible) {
      hideSnackbar();
    }
  }, [hideSnackbar, couponModalVisible]);

  useEffect(() => {
    isLogged && handleUserFetch();
  }, [handleUserFetch, isLogged]);

  return (
    <>
      <ScrollView>
        <Pressable
          onPress={() => {
            if (couponModalVisible) {
              hideSnackbar();
            }
          }}
        >
          {loadingCoupon && (
            <Block alignCenter justifyCenter style={styles.loadingCoupon}>
              <Loader color={palette.common.gray} />
            </Block>
          )}

          <Block paddingVertical={isWeb ? 4 : 2}>
            <Typography variant="newH2">{t("screens.coupons.title")}</Typography>
            <Block paddingTop={3}>
              <Typography variant="paragraph">{t("screens.coupons.description")}</Typography>
            </Block>

            <Block paddingVertical={2}>
              <Input
                onChangeText={(text) => {
                  setCode(text);
                  if (couponModalVisible) {
                    hideSnackbar();
                  }
                }}
                onFocus={() => hideSnackbar()}
                placeholder={t("screens.coupons.typeCode")}
                ref={inputRef}
                value={code}
              />
              <Button
                disabled={isDisabled}
                label={t("screens.redeemCoupon.add")}
                onPress={handleRedeemCoupon}
                type="primary"
              />
            </Block>

            <StepappWallet
              isHeadlineVisible
              balance={virtualWalletBalance?.getVirtualWalletBalance?.balance || 0}
              onAddPress={handleOnAddPress}
            />

            <Block paddingVertical={2.5}>
              <Divider />
            </Block>

            <Block marginBottom={4}>
              <Typography variant="sectionTitle">{t("screens.coupons.yourDiscounts")}</Typography>
            </Block>

            {loading ? (
              <Loader />
            ) : coupons?.length > 0 ? (
              coupons.map((item) => (
                <CouponItem key={item.id} {...item} onPress={handleCouponPress} />
              ))
            ) : (
              <Typography type="secondary" variant="paragraph">
                {t("screens.coupons.emptyCoupons")}
              </Typography>
            )}
          </Block>
        </Pressable>
      </ScrollView>
      <Modal
        description={description}
        primaryAction={() => setCouponModalVisible(false)}
        primaryActionLabel={couponModalType === "success" ? t("common.OK") : t("common.tryAgain")}
        title={title}
        toggle={() => setCouponModalVisible(false)}
        visible={isWeb && couponModalVisible}
      />
      <Snackbar
        containerStyles={styles.customSnackbar}
        isVisible={!isWeb && couponModalVisible}
        ref={snackbarRef}
        setIsVisible={setCouponModalVisible}
        text={title}
        type={couponModalType}
      />
    </>
  );
};

export default CouponsScreen;

const styles = StyleSheet.create({
  customSnackbar: {
    bottom: spacing.unit * 6,
    marginHorizontal: -spacing.unit * 2.5,
  },
  loadingCoupon: {
    backgroundColor: palette.common.white,
    bottom: 0,
    left: 0,
    opacity: 0.8,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 100,
  },
});
